import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Artikel, ArtikelFelder, Steuersatz, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { formatDatum } from "../format.js";
import { MarkdownHinweis } from "./MarkdownHinweis.js";
import "./ArtikelDialog.css";

/**
 * Der Artikeldialog — EINE Komponente für die Verwaltung (Artikelstamm: anlegen UND
 * ändern) und den Artikelkatalog (ADMIN-Kurzweg: nur ändern). Herausgelöst aus
 * Verwaltung.tsx (04.09.), als der Katalog denselben Dialog brauchte: Ein zweiter,
 * nachgebauter Dialog wäre die sicherste Art, dass dasselbe Formular an zwei Bildschirmen
 * Verschiedenes bedeutet (Muster: PositionsEditor).
 *
 * Eigener CSS-Block `adlg` — mit KEINER Ansicht geteilt (Lektion aus `.ka__`, siehe
 * ArtikelDialog.css). Die Bedienlogik entspricht den übrigen Verwaltungsdialogen: kein
 * Versionsvergleich (last-write-wins), die Fachprüfung (Pflichtfelder, Längen, die
 * 20.000-Zeichen-Grenze der Beschreibung, Preisgrenze) macht der SERVER — hier steht nur,
 * was die Maske selbst wissen muss (Preis als Zahl, Steuersatz gewählt).
 *
 * Die Felder stehen ZWEISPALTIG (Wunsch vom 04.09.), die Beschreibung darunter über die
 * volle Breite als echtes, mehrzeiliges Textfeld — ein `<input>` schluckt Zeilenumbrüche.
 * Auf schmalen Bildschirmen fällt das Raster auf eine Spalte zurück (siehe CSS).
 *
 * WER den Feldsatz wohin schreibt, entscheidet der Aufrufer über `onSpeichern`: Die
 * Verwaltung legt an oder ändert, der Katalog ändert ausschliesslich — so braucht der
 * Katalog keinen Anlege-Weg in seinem Klienten, nur weil der Dialog einen kennt.
 */

export type ArtikelDialogSpeichern = {
  /** null = neuer Artikel (nur die Verwaltung öffnet den Dialog so). */
  id: string | null;
  /** Beim Anlegen die Wahl aus dem Dialog; beim Ändern unverändert der Bestand. */
  companyId: string;
  felder: ArtikelFelder;
};

export type ArtikelDialogProps = {
  /** null = neuen Artikel anlegen; sonst wird DIESER Artikel geändert. */
  artikel: Artikel | null;
  companies: Unternehmen[];
  /** Die bekannten Steuersätze — für die Auswahlliste (nur aktive) und die Vorbelegung. */
  steuersaetze: Steuersatz[];
  /** Vorbelegung beim Anlegen; beim Ändern kommen beide Werte aus `artikel`. */
  vorgabe: { companyId: string; taxRateId: string };
  /**
   * Schreibt den Feldsatz — wirft bei Ablehnung (der Dialog zeigt eine 422 an den
   * Feldern, alles andere als Sammelmeldung) und kehrt bei Erfolg still zurück.
   */
  onSpeichern: (eingabe: ArtikelDialogSpeichern) => Promise<void>;
  /** Nach ERFOLGREICHEM Speichern — der Aufrufer schliesst den Dialog und liest neu. */
  onGespeichert: () => void;
  /**
   * Der EINE Weg aus dem Panel neben dem Speichern: der Abbrechen-Knopf und die
   * Escape-Taste (Hausregel „striktes Modalpanel", 06.09.2026). Ein Klick auf den
   * Hintergrund ruft ihn NICHT mehr — er schliesst gar nicht.
   *
   * Die Rückfrage bei ungespeicherten Eingaben stellt der Dialog selbst, bevor er ihn
   * ruft: Nur er kennt seinen Entwurf. Der Aufrufer bekommt also einen Ruf, der schon
   * entschieden ist, und räumt bloss noch auf.
   */
  onAbbrechen: () => void;
};

type Quelle = "" | "MONITOR" | "AIRCALL";

type Entwurf = {
  companyId: string;
  productNumber: string;
  name: string;
  description: string;
  unit: string;
  netUnitPrice: string;
  taxRateId: string;
  category: string;
  /** „Abrechnungsdaten aus" — "" heisst keine (Spec „Abrechnungsquellen"). */
  billingDataSource: Quelle;
};

type FeldName = "productNumber" | "name" | "description" | "unit" | "netUnitPrice"
  | "taxRateId" | "category" | "billingDataSource";

const FELDNAMEN: readonly FeldName[] = ["productNumber", "name", "description", "unit",
  "netUnitPrice", "taxRateId", "category", "billingDataSource"];

/** Die drei Werte der Auswahl, in der Reihenfolge der Spec. */
const QUELLEN: readonly { wert: Quelle; text: string }[] = [
  { wert: "", text: "keine" }, { wert: "MONITOR", text: "Monitor" },
  { wert: "AIRCALL", text: "Aircall" },
];

const SATZ_FORMAT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

function entwurfAus(
  a: Artikel | null, vorgabe: { companyId: string; taxRateId: string },
): Entwurf {
  if (a === null) {
    return {
      companyId: vorgabe.companyId, productNumber: "", name: "", description: "",
      unit: "", netUnitPrice: "", taxRateId: vorgabe.taxRateId, category: "",
      billingDataSource: "",
    };
  }
  return {
    companyId: a.companyId, productNumber: a.productNumber, name: a.name,
    description: a.description ?? "", unit: a.unit, netUnitPrice: String(a.netUnitPrice),
    taxRateId: a.taxRateId, category: a.category ?? "",
    billingDataSource: a.billingDataSource ?? "",
  };
}

function entwurfGleich(a: Entwurf, b: Entwurf): boolean {
  return a.companyId === b.companyId && a.productNumber === b.productNumber
    && a.name === b.name && a.description === b.description && a.unit === b.unit
    && a.netUnitPrice === b.netUnitPrice && a.taxRateId === b.taxRateId
    && a.category === b.category && a.billingDataSource === b.billingDataSource;
}

// Bewusst benannte Doppelungen zu Verwaltung.tsx (dort für die übrigen Bereiche weiter
// gebraucht) — dieselbe Abwägung wie bei formatKonfliktZeit dort: Sie zusammenzulegen
// hiesse, einen gemeinsamen Formular-Hilfsbaustein zu bauen (nach @kundeninfo/ui
// gehörend), eine eigene Entscheidung samt Umbau aller Aufrufer.

const KEINE_ZAHL = Symbol("keine Zahl");

/** Text zu Zahl — leer bleibt leer, Komma zählt als Dezimaltrennzeichen. */
function alsZahl(eingabe: string): number | null | typeof KEINE_ZAHL {
  const roh = eingabe.trim();
  if (roh === "") return null;
  const zahl = Number(roh.replace(",", "."));
  return Number.isFinite(zahl) ? zahl : KEINE_ZAHL;
}

/** Die Meldung eines Fehlers — ApiError bringt eine mit, alles andere bekommt die
 *  angegebene Ersatzmeldung. */
function meldungAus(fehler: unknown, ersatz: string): string {
  return fehler instanceof ApiError ? fehler.message : ersatz;
}

/** Die Feldfehler einer 422, eingeschränkt auf die Felder dieses Dialogs. */
function feldFehlerAus(
  fehler: ApiError,
): { felder: Partial<Record<FeldName, string>>; unzuordenbar: boolean } {
  const felder: Partial<Record<FeldName, string>> = {};
  let unzuordenbar = false;
  for (const f of fehler.fields ?? []) {
    const treffer = FELDNAMEN.find((e) => e === f.field);
    if (treffer === undefined) { unzuordenbar = true; continue; }
    if (felder[treffer] === undefined) felder[treffer] = f.message;
  }
  return { felder, unzuordenbar };
}

export function ArtikelDialog(p: ArtikelDialogProps): ReactElement {
  const [entwurf, setEntwurf] = useState<Entwurf>(() => entwurfAus(p.artikel, p.vorgabe));
  const [feldFehler, setFeldFehler] = useState<Partial<Record<FeldName, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  const geaendert = !entwurfGleich(entwurf, entwurfAus(p.artikel, p.vorgabe));
  useBeforeUnload(geaendert);

  /**
   * Abbrechen — mit Rückfrage, wenn etwas Ungespeichertes im Panel steht (Hausregel
   * „striktes Modalpanel", 06.09.2026; derselbe Wortlaut wie beim Verlassen-Wächter des
   * Routers, denn es ist derselbe Verlust). Escape geht denselben Weg, der Rahmen ruft
   * dieselbe Funktion.
   *
   * Der Riegel während des Schreibens liegt am Rahmen (`abbrechenGesperrt` unten) und gilt
   * damit für Knopf UND Taste; die Zeile hier ist die zweite Hälfte desselben Riegels für
   * den Fall, dass diese Funktion aus dem Inhalt heraus gerufen wird.
   */
  function abbrechen(): void {
    if (arbeitet) return;
    if (!darfAbbrechen(geaendert)) return;
    p.onAbbrechen();
  }

  const neu = p.artikel === null;
  const titel = neu ? "Neuer Artikel" : "Artikel ändern";

  function setze<K extends keyof Entwurf>(feld: K, wert: Entwurf[K]): void {
    setEntwurf((e) => ({ ...e, [feld]: wert }));
  }

  /** Nur aktive Zeitscheiben stehen zur Auswahl — ein stillgelegter Satz soll sich nicht
   *  neu vergeben lassen. Eine bereits STILLGELEGTE Vorgabe bleibt sichtbar, damit das
   *  Ändern eines anderen Feldes sie nicht stillschweigend auf einen anderen Satz
   *  umstellt. */
  const waehlbareSaetze = p.steuersaetze.filter((s) => s.active);
  const angeboten = waehlbareSaetze.some((s) => s.id === entwurf.taxRateId)
    ? waehlbareSaetze
    : p.steuersaetze.filter((s) => s.active || s.id === entwurf.taxRateId);
  // Der Katalog kennt nur die AKTIVEN Sätze: Zeigt ein Artikel auf eine inzwischen
  // abgelöste Scheibe, hält diese Zeile den Bestand fest, statt ihn beim nächsten
  // Speichern still auf den ersten angebotenen Satz umzustellen.
  const vorgabeUnbekannt = entwurf.taxRateId !== ""
    && !angeboten.some((s) => s.id === entwurf.taxRateId);

  function feldsatz(): ArtikelFelder | null {
    const netUnitPrice = alsZahl(entwurf.netUnitPrice);
    if (netUnitPrice === KEINE_ZAHL || netUnitPrice === null) {
      setFeldFehler({ netUnitPrice: "Bitte einen Preis eintragen." });
      return null;
    }
    if (entwurf.taxRateId === "") {
      setFeldFehler({ taxRateId: "Bitte einen Steuersatz wählen." });
      return null;
    }
    // Die Fachprüfung (Pflichtfelder, Längen, Preisgrenze) macht der Server — hier steht
    // nur, was die Maske selbst wissen muss.
    return {
      productNumber: entwurf.productNumber,
      name: entwurf.name,
      description: entwurf.description.trim() === "" ? null : entwurf.description,
      unit: entwurf.unit,
      netUnitPrice,
      taxRateId: entwurf.taxRateId,
      category: entwurf.category.trim() === "" ? null : entwurf.category,
      // Die Maske schickt die Quelle IMMER mit — sie kennt das Feld (Ruling R-ARTIKEL gilt
      // für Aufrufer, die es nicht kennen).
      billingDataSource: entwurf.billingDataSource === "" ? null : entwurf.billingDataSource,
    };
  }

  async function speichern(): Promise<void> {
    setFeldFehler({});
    setMeldung(null);
    const felder = feldsatz();
    if (felder === null) return;
    setArbeitet(true);
    try {
      await p.onSpeichern({
        id: p.artikel === null ? null : p.artikel.id,
        companyId: entwurf.companyId,
        felder,
      });
      p.onGespeichert();
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.status === 422) {
        const { felder: gefunden, unzuordenbar } = feldFehlerAus(fehler);
        setFeldFehler(gefunden);
        if (unzuordenbar || Object.keys(gefunden).length === 0) setMeldung(fehler.message);
      } else {
        setMeldung(meldungAus(fehler, "Unerwarteter Fehler beim Speichern."));
      }
    } finally {
      setArbeitet(false);
    }
  }

  return (
    /* Rahmen, Titel und Fußleiste kommen aus dem gemeinsamen Baustein (@kundeninfo/ui) —
       die Hausregel „striktes Modalpanel" steht dort an einer Stelle für alle Masken. Die
       Breite `mittel` ist die des zweispaltigen Feldrasters (Wunsch vom 04.09.). */
    <Modal titel={titel} onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}
      breite="mittel">
      {meldung !== null && <div className="adlg__meldung" role="alert">{meldung}</div>}

      <div className="adlg__raster">
        {neu ? (
          <div className="adlg__auswahl adlg__voll">
            <label className="adlg__label" htmlFor="adlg-unternehmen">
              Unternehmen des Artikels
            </label>
            <select id="adlg-unternehmen" className="adlg__select"
              value={entwurf.companyId}
              onChange={(e) => setze("companyId", e.target.value)}>
              {p.companies.map((c) => (
                <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
              ))}
            </select>
          </div>
        ) : (
          // Beim Ändern nur noch anzeigen: Ein Artikel lässt sich nicht umhängen (seine
          // Nummer ist je Unternehmen eindeutig), der Server nähme das Feld gar nicht an.
          <div className="adlg__voll">
            <Feld id="adlg-unternehmen-fest" label="Unternehmen des Artikels"
              bearbeitbar={false}
              wert={p.companies.find((c) => c.id === entwurf.companyId)?.name ?? "—"}
              onChange={() => undefined} />
          </div>
        )}

        <Feld id="adlg-nummer" label="Artikelnummer" bearbeitbar
          wert={entwurf.productNumber}
          onChange={(w) => setze("productNumber", w)}
          {...(feldFehler.productNumber !== undefined
            ? { fehler: feldFehler.productNumber } : {})} />
        <Feld id="adlg-name" label="Name" bearbeitbar wert={entwurf.name}
          onChange={(w) => setze("name", w)}
          {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
        <Feld id="adlg-einheit" label="Einheit" bearbeitbar wert={entwurf.unit}
          onChange={(w) => setze("unit", w)}
          {...(feldFehler.unit !== undefined ? { fehler: feldFehler.unit } : {})} />
        <Feld id="adlg-preis" label="Nettopreis je Einheit" bearbeitbar
          wert={entwurf.netUnitPrice}
          onChange={(w) => setze("netUnitPrice", w)}
          {...(feldFehler.netUnitPrice !== undefined
            ? { fehler: feldFehler.netUnitPrice } : {})} />
        <div className="adlg__auswahl">
          <label className="adlg__label" htmlFor="adlg-steuer">Steuersatz</label>
          <select id="adlg-steuer" className="adlg__select" value={entwurf.taxRateId}
            aria-invalid={feldFehler.taxRateId !== undefined ? true : undefined}
            onChange={(e) => setze("taxRateId", e.target.value)}>
            {vorgabeUnbekannt && (
              <option value={entwurf.taxRateId}>bisheriger Steuersatz</option>
            )}
            {angeboten.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({SATZ_FORMAT.format(s.ratePercent)} %, ab {
                  formatDatum(s.validFrom)})
              </option>
            ))}
          </select>
          {feldFehler.taxRateId !== undefined && (
            <span className="adlg__feldfehler" role="alert">{feldFehler.taxRateId}</span>
          )}
        </div>
        <Feld id="adlg-kategorie" label="Kategorie (optional)" bearbeitbar
          wert={entwurf.category}
          onChange={(w) => setze("category", w)}
          {...(feldFehler.category !== undefined
            ? { fehler: feldFehler.category } : {})} />

        {/* „Abrechnungsdaten aus" (Spec „Abrechnungsquellen") — unter der Kategorie. Die
            Position erbt die Quelle live über ihren Artikel; eine Übersteuerung je
            Position gibt es nicht (Entscheid 24.09.2026). */}
        <div className="adlg__auswahl">
          <label className="adlg__label" htmlFor="adlg-quelle">Abrechnungsdaten aus</label>
          <select id="adlg-quelle" className="adlg__select" value={entwurf.billingDataSource}
            aria-invalid={feldFehler.billingDataSource !== undefined ? true : undefined}
            onChange={(e) => setze("billingDataSource", e.target.value as Quelle)}>
            {QUELLEN.map((q) => (
              <option key={q.wert} value={q.wert}>{q.text}</option>
            ))}
          </select>
          {feldFehler.billingDataSource !== undefined && (
            <span className="adlg__feldfehler" role="alert">
              {feldFehler.billingDataSource}
            </span>
          )}
        </div>

        {/* Ganz unten über die volle Breite: Die Beschreibung ist der eine LANGE Wert
            (bis 20.000 Zeichen, siehe domain) — ein echtes, mehrzeiliges Textfeld,
            denn ein <input> schluckt Zeilenumbrüche. */}
        <div className="adlg__auswahl adlg__voll">
          <label className="adlg__label" htmlFor="adlg-beschreibung">
            Beschreibung (optional)
          </label>
          <textarea id="adlg-beschreibung" className="adlg__textarea" rows={6}
            value={entwurf.description}
            aria-invalid={feldFehler.description !== undefined ? true : undefined}
            aria-describedby={feldFehler.description !== undefined
              ? "adlg-beschreibung-fehler" : undefined}
            onChange={(e) => setze("description", e.target.value)} />
          {feldFehler.description !== undefined && (
            <span id="adlg-beschreibung-fehler" className="adlg__feldfehler"
              role="alert">
              {feldFehler.description}
            </span>
          )}
          {/* Die Beschreibung wird als Markdown gesetzt — in den Artikellisten (dort mit
              `kn-md-anriss` auf drei Zeilen geklemmt), im Detail und, sobald sie als
              Positionsbeschreibung in einen Beleg wandert, auch im PDF. */}
          <MarkdownHinweis />
        </div>
      </div>

      <ModalFuss speichernText="Speichern" speichernGesperrt={arbeitet}
        abbrechenGesperrt={arbeitet}
        onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}
